import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { AlertTriangle, Calendar, ShoppingBag, User as UserIcon, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../Shared/api';
import { useLocale } from '../../Shared/locale';
import { Modal } from '../../Shared/Modal/Modal';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Campaign, CampaignField, CampaignFields, CampaignType, Charity, Event, Project, TeamMember, User } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import { ConfirmButton } from '../../Shared/UI/ConfirmButton';
import Delimit from '../../Shared/UI/Delimit';
import { Icon } from '../../Shared/UI/Icon';
import { PickerItem } from '../../Shared/UI/Picker';
import { useRouter } from '../router';
import ProjectIcon from '../Settings/Projects/ProjectIcon';
import { Product } from '../types';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import AddOfflineDonationForm from './AddOfflineDonationForm';
import { CampaignLayout } from './CampaignLayout';
import EditCampaignForm from './EditCampaignForm';

export interface CampaignPageProps {
  campaign: Campaign & CampaignFields & {
    project: Project;
    event: Event | null;
    team: Campaign | null;
    user: User;
    donations_count: number;
    campaign_products: Product[];
    deletable: boolean;
    user_deleted_at: string | null;
    user_deleted_reason: string | null;
  };
  teamMembers: TeamMember[];
  projects: (PickerItem & {
    allowed_fields: CampaignField[];
    private: boolean;
  })[];
  charity: Charity;
}

export default function CampaignPage({
  campaign,
  teamMembers,
  projects,
  charity,
}: CampaignPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();
  const { formatCurrency, formatDate, formatNumber } = useLocale();

  const [showOfflineDonationModal, setShowOfflineDonationModal] = useState(false);

  const [editCampaign, { errors, loading, formKey }] = useMutation(
    routes.edit_campaign(campaign.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:campaign_saved'));
      },
    },
  );

  const [deleteCampaign, { loading: deleting }] = useMutation(
    routes.delete_campaign(campaign.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:campaign_deleted'));
      },
      onError: () => {
        notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
      },
    });

  return (
    <>
      <Container>
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-3 space-y-4">
            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:project')}
              </h4>
              <Link
                href={routes.project_page(campaign.project.id)}
                className={classNames({ 'opacity-50': !campaign.project.enabled })}
              >
                <ProjectIcon project={campaign.project} className="mr-1" />
                {campaign.project.title}
              </Link>
            </div>
            {campaign.event && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:event')}
                </h4>
                <Link href={routes.event_page(campaign.event.id)}>
                  <Icon className="mr-1">
                    <Calendar />
                  </Icon>
                  {campaign.event.title}
                </Link>
              </div>
            )}

            {campaign.team && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:team')}
                </h4>
                <Link
                  href={routes.campaign_page(campaign.team.id)}
                  className={classNames({ 'opacity-50': !campaign.team.enabled })}
                >
                  <Icon className="mr-1">
                    <Users />
                  </Icon>
                  {campaign.team.title}
                </Link>
              </div>
            )}

            {campaign.type === CampaignType.Team && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:team_members')}
                </h4>
                {teamMembers.length === 0 && t('backend:none')}
                {teamMembers.map((member) => (
                  <div className={classNames({ 'opacity-50': !member.enabled })} key={member.id}>
                    <Link href={routes.campaign_page(member.id)}>
                      <Icon className="mr-1">
                        <UserIcon />
                      </Icon>
                      {member.title}
                    </Link>
                  </div>
                ))}
              </div>
            )}

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:donations')}
              </h4>
              {formatNumber(campaign.donations_count)}
              {campaign.donations_count > 0 && (
                <span className="text-slate-400">
                  {' · '}
                  <Link href={`${routes.donations_page()}?project_id=${campaign.project.id}&campaign_id=${campaign.id}`}>
                    {t('backend:view')}
                  </Link>
                </span>
              )}
            </div>

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:raised')}
              </h4>
              {formatCurrency(campaign.funds_raised)}
              {campaign.fundraising_target && (
                <>
                  {' / '}
                  {formatCurrency(campaign.fundraising_target)}
                </>
              )}
            </div>

            {campaign.campaign_products.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-500">
                  {t('backend:products')}
                </h4>
                {campaign.campaign_products.length === 0 && t('backend:none')}
                {campaign.campaign_products.map((product) => (
                  <div className={classNames({ 'opacity-50': !product.enabled })} key={product.id}>
                    <Link href={routes.product_page(product.id)}>
                      <Icon className="mr-1">
                        <ShoppingBag />
                      </Icon>
                      {product.title}
                    </Link>
                  </div>
                ))}
              </div>
            )}

            <div>
              <h4 className="font-medium text-slate-500">
                {t('backend:created')}
              </h4>
              {formatDate(campaign.created_at, 'display_date_time')}
            </div>

            {!campaign.enabled && campaign.user_deleted_at && (
              <div>
                <h4 className="font-medium text-red-600">
                  {t('backend:deactivated_by_user')}
                </h4>
                <Delimit>
                  {formatDate(campaign.user_deleted_at, 'display_date_time')}
                  {campaign.user_deleted_reason}
                </Delimit>
              </div>
            )}

            <div>
              <Button
                onClick={() => setShowOfflineDonationModal(true)}
                variant="secondary"
                size="sm"
              >
                {t('backend:add_offline_donation')}
              </Button>
            </div>

            {campaign.deletable && (
              <div>
                <ConfirmButton
                  onClick={() => deleteCampaign()}
                  loading={deleting}
                  disabled={!campaign.deletable}
                  variant="danger"
                  size="sm"
                >
                  {t('backend:delete_campaign')}
                </ConfirmButton>
              </div>
            )}
          </div>

          <div className="col-span-9">
            <EditCampaignForm
              campaign={campaign}
              projects={projects}
              charity={charity}
              onSubmit={editCampaign}
              serverErrors={errors}
              loading={loading}
              key={formKey}
            />
          </div>
        </div>
      </Container>

      <Modal isOpen={showOfflineDonationModal} onClose={() => setShowOfflineDonationModal(false)} size="sm">
        <AddOfflineDonationForm campaign={campaign} />
      </Modal>
    </>
  );
}

CampaignPage.layout = (page: ReactNode) => <Layout><CampaignLayout>{page}</CampaignLayout></Layout>;
